import React, { useState, useEffect } from 'react';
import { FaBook, FaMapMarkerAlt, FaEye, FaCalendar, FaChevronLeft, FaChevronRight, FaTag } from 'react-icons/fa';
import SEO from './SEO';
import BASE_URL, { SEO_BASE_URL } from '../config';
import { getBlogArticles, getBlogCategories } from '../api/client';
import BlogSubscribeForm from './BlogSubscribeForm';
import TryoutCTA from './landing/TryoutCTA';
import InternalLinks from './InternalLinks';

const CATEGORY_LABELS = {
  guide: 'Destination Guide',
  tips: 'Travel Tips',
  itinerary: 'Sample Itinerary',
  news: 'News',
  comparison: 'Comparison',
};

/** Read ?destination= — Destinations.js and the related-articles block both
 *  link here with that filter applied. */
function destinationFromUrl() {
  try {
    return new URLSearchParams(window.location.search).get('destination') || null;
  } catch {
    return null;
  }
}

const BlogList = ({ onBack, onViewArticle, onGetStarted, onViewTryout, onNavigate }) => {
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeDestination, setActiveDestination] = useState(destinationFromUrl);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticles();
  }, [page, activeCategory, activeDestination]);

  useEffect(() => {
    getBlogCategories()
      .then(data => setCategories(data.categories || []))
      .catch(() => {});
  }, []);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12 };
      if (activeCategory) params.category = activeCategory;
      if (activeDestination) params.destination = activeDestination;
      const data = await getBlogArticles(params);
      setArticles(data.articles || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) {
      console.error('Failed to load articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <SEO
        title="Travel Guides & Blog"
        description="Expert travel guides, destination itineraries, and trip planning tips powered by AI. Discover the best routes, hidden gems, and travel advice."
        canonical={`${SEO_BASE_URL}/blog`}
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {onBack && (
            <button onClick={onBack} className="flex items-center gap-2 text-neutral-600 hover:text-primary-600 font-medium transition-colors">
              <span>&larr;</span> Back
            </button>
          )}
          {!onBack && <div></div>}
          <button
            onClick={onGetStarted}
            className="bg-gradient-to-r from-primary-500 via-accent-500 to-adventure-500 text-white px-6 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-glow-primary hover:scale-105 transition-all duration-300 animate-gradient-x bg-[length:200%_auto]"
          >
            Get Started Free
          </button>
        </div>
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">
          {activeDestination ? `Travel Guides — ${activeDestination}` : 'Travel Guides & Blog'}
        </h1>
        <p className="text-neutral-600">Destination guides, itinerary ideas, and travel tips to help you plan your next adventure.</p>
        {activeDestination && (
          <button
            onClick={() => {
              setActiveDestination(null);
              setPage(1);
              window.history.replaceState({}, '', '/blog');
            }}
            className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full hover:bg-primary-100 transition-colors"
          >
            <FaMapMarkerAlt className="text-xs" /> {activeDestination}
            <span className="text-neutral-400">&times;</span>
          </button>
        )}
      </div>

      {onViewTryout && <TryoutCTA onViewTryout={onViewTryout} className="mb-8" />}

      {/* Category filters */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => { setActiveCategory(null); setPage(1); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              !activeCategory ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            All ({total})
          </button>
          {categories.map(cat => (
            <button
              key={cat.name}
              onClick={() => { setActiveCategory(cat.name); setPage(1); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === cat.name ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              {CATEGORY_LABELS[cat.name] || cat.name} ({cat.count})
            </button>
          ))}
        </div>
      )}

      {/* Articles grid */}
      {loading ? (
        <div className="text-center py-16 text-neutral-400">Loading articles...</div>
      ) : articles.length === 0 ? (
        <div className="text-center py-16">
          <FaBook className="text-5xl text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-500 text-lg">No articles yet. Check back soon!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map(article => (
            <article
              key={article.id}
              onClick={() => onViewArticle && onViewArticle(article.slug)}
              className="bg-white rounded-xl border border-neutral-200 overflow-hidden hover:shadow-lg hover:border-primary-300 transition-all cursor-pointer group"
            >
              {article.featured_image && (
                <div className="h-48 overflow-hidden">
                  <img
                    src={article.featured_image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-primary-600 bg-primary-50 px-2.5 py-0.5 rounded-full">
                    {CATEGORY_LABELS[article.category] || article.category}
                  </span>
                  {article.destination && (
                    <span className="text-xs text-neutral-500 flex items-center gap-1">
                      <FaMapMarkerAlt className="text-[10px]" /> {article.destination}
                    </span>
                  )}
                </div>
                <h2 className="text-lg font-bold text-neutral-800 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                  {article.title}
                </h2>
                {article.excerpt && (
                  <p className="text-sm text-neutral-600 mb-3 line-clamp-3">{article.excerpt}</p>
                )}
                <div className="flex items-center justify-between text-xs text-neutral-400">
                  <span className="flex items-center gap-1">
                    <FaCalendar /> {formatDate(article.published_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <FaEye /> {article.view_count} views
                  </span>
                </div>
                {article.tags && article.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {article.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="text-[11px] text-neutral-400 bg-neutral-50 px-2 py-0.5 rounded">
                        <FaTag className="inline text-[9px] mr-0.5" />{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-10">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="flex items-center gap-1 px-4 py-2 rounded-lg bg-neutral-100 text-neutral-600 hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <FaChevronLeft className="text-xs" /> Previous
          </button>
          <span className="text-sm text-neutral-500">Page {page} of {pages}</span>
          <button
            onClick={() => setPage(p => Math.min(pages, p + 1))}
            disabled={page === pages}
            className="flex items-center gap-1 px-4 py-2 rounded-lg bg-neutral-100 text-neutral-600 hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next <FaChevronRight className="text-xs" />
          </button>
        </div>
      )}

      {/* Subscribe form */}
      <BlogSubscribeForm />

      {/* Internal Links for SEO */}
      <InternalLinks currentPage="blog" onNavigate={onNavigate} variant="compact" />
    </div>
  );
};

export default BlogList;
